import { Injectable } from '@angular/core';
import { countries, Country } from '../../../list/countries';
import { Province, provinces } from '../../../list/provinces';
import { LocaleService } from './locale.service';
import anyAscii from 'any-ascii';

@Injectable()
export class CountriesService {

    public defaultCountry = 'CA';

    constructor(private localeService: LocaleService) {
    }

    countryName(id: string): string {
        id = id.toUpperCase();
        const match = countries.find((country) => country.code === id);
        const locale = this.localeService.getSupportedUserLocale();
        return match ? match.name : null;
    }

    /**
     * Return an array of country codes to display$ filtered by the text provided.
     * We return all countries when the input is empty otherwise
     *
     * @param text Text as entered by the user
     * @param limit Maximum number of entries to return
     * @return string[] Return an array of country codes
     */
    countryFilter(text?: string, limit = 5): string[] {
        if (text === null || text === undefined || text.length === 0) {
            return countries.map((country) => country.code);
        }
        const table = {};
        countries.forEach((country) => {
            const score = this.matchCountry(country, text);
            if (score) {
                if (!table.hasOwnProperty(score)) {
                    table[score] = [];
                }
                table[score].push(country.code);
            }
        });
        const output = [];
        for (let i = 1; i <= 5; i++) {
            if (table.hasOwnProperty(i)) {
                for (const code of table[i]) {
                    output.push(code);
                    if (output.length === limit) {
                        return output;
                    }
                }
            }
        }
        return output;
    }

    resolveCountryCode(text: string, fallback: string): string {
        text = text ? text.toUpperCase() : '';
        if (text === '') {
            text = fallback;
        }
        if (text === '') {
            return 'CA'; // TODO: map this to customer default country
        }
        let match = countries.find((country) => country.code === text);
        if (match) {
            return match.code;
        }
        match = countries.find((country) => country.name.toUpperCase().startsWith(text));
        if (match) {
            return match.code;
        }
        return fallback;
    }

    resolveProvinceCode(countryId: string, text: string, fallback: string): string {
        text = text.toUpperCase();
        const match = this.getProvinces(countryId).find((province) => province.code === text);
        if (match) {
            return match.code;
        }
        return fallback;
    }

    isCountryCode(code: string): boolean {
        return !!this.countryName(code);
    }

    isProvinceCode(countryId: string, code: string): boolean {
        if (provinces[countryId]) {
            return !!this.getProvince(countryId, code);
        }
        return false;
    }

    codeForProvinceName(countryId, name): string {
        return '';
    }

    codeForCountryName(name): string {
        return '';
    }

    displayProvincesFor(id): boolean {
        return id === 'CA' || id === 'US';
    }

    displayPostalCodesFor(id): boolean {
        return true;
    }

    getProvinces(countryId): Province[] {
        if (provinces.hasOwnProperty(countryId)) {
            return provinces[countryId];
        }
        return [];
    }

    getProvince(countryId, provinceId): Province {
        if (provinces.hasOwnProperty(countryId)) {
            return provinces[countryId].find((province) => province.code === provinceId);
        }
        return null;
    }

    provinceName(countryId, provinceId): string {
        const match = this.getProvinces(countryId)
            .find((province) => province.code === provinceId);
        if (match == null) {
            return '';
        }
        const locale = this.localeService.getSupportedUserLocale();
        if (match.localization && match.localization.hasOwnProperty(locale)) {
            return match.localization[locale];
        }
        return match.name;
    }

    protected matchCountry(country: Country, term: string): number {
        term = term.toUpperCase();
        if (country.code === term) {
            return 1;
        }
        if (country.name.toUpperCase().startsWith(term)) {
            return 3;
        }
        if (term.length >= 3) {
            if (country.name.toUpperCase().indexOf(term) !== -1) {
                return 5;
            }
        }
        return 0;
    }

    protected matchProvince(province: Province, term: string): number {
        term = anyAscii(term.toUpperCase());
        if (province.code === term) {
            return 1;
        }
        const locale = this.localeService.getSupportedUserLocale();
        if (province.localization && province.localization.hasOwnProperty(locale)) {
            if (anyAscii(province.localization[locale]).toUpperCase().startsWith(term)) {
                return 2;
            }
        }
        if (anyAscii(province.name).toUpperCase().startsWith(term)) {
            return 3;
        }
        if (term.length >= 3) {
            if (province.localization && province.localization.hasOwnProperty(locale)) {
                if (anyAscii(province.localization[locale]).toUpperCase().indexOf(term) !== -1) {
                    return 4;
                }
            }
            if (anyAscii(province.name).toUpperCase().indexOf(term) !== -1) {
                return 5;
            }
        }
        return 0;
    }

    provinceFilter(countryId, term, limit = 5): string[] {
        term = term.toUpperCase();
        const table = {};
        this.getProvinces(countryId).forEach((province) => {
            const score = this.matchProvince(province, term);
            if (score) {
                if (!table.hasOwnProperty(score)) {
                    table[score] = [];
                }
                table[score].push(province.code);
            }
        });
        const output = [];
        for (let i = 1; i <= 5; i++) {
            if (table.hasOwnProperty(i)) {
                for (const code of table[i]) {
                    output.push(code);
                    if (output.length === limit) {
                        return output;
                    }
                }
            }
        }
        return output;
    }

    searchAllProvinces(term: string, limit = 5): Province[] {
        const output = [];
        for (const countryId of Object.keys(provinces)) {
            const matches = this.provinceFilter(countryId, term);
            for (const code of matches) {
                output.push(this.getProvince(countryId, code));
                if (output.length == limit) {
                    return output;
                }
            }
        }
        return output;
    }

}
